import { useState, useEffect, useCallback } from "react";
import {
  Save, Loader2, CheckCircle, AlertCircle, ExternalLink, Crown,
  Plus, X, Music, Upload, Camera
} from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

export const ArtistProfileTab = ({ currentUser, authToken }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [msg, setMsg] = useState(null);
  const [tagInput, setTagInput] = useState("");

  const [form, setForm] = useState({
    bio: "",
    photo_url: "",
    location: "",
    gender: "",
    genre_tags: [],
    instagram: "",
    spotify: "",
    soundcloud: "",
    youtube: "",
    tiktok: "",
    facebook: "",
    twitter: "",
    website: "",
  });

  const [uploading, setUploading] = useState(false);

  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState(currentUser?.name || "");

  const headers = { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" };

  const fetchProfile = useCallback(async () => {
    try {
      const email = currentUser?.email;
      if (!email) return;
      // Find profile by email
      const listRes = await fetch(`${BACKEND_URL}/api/artists?email=${encodeURIComponent(email)}`);
      if (!listRes.ok) return;
      const matches = await listRes.json();
      if (matches.length === 0) return;

      // Fetch full profile
      const profileRes = await fetch(`${BACKEND_URL}/api/artists/${matches[0].slug}`);
      if (!profileRes.ok) return;
      const match = await profileRes.json();

      setProfile(match);
      setForm({
        bio: match.bio || "",
        photo_url: match.photo_url || "",
        location: match.location || "",
        gender: match.gender || "",
        genre_tags: match.genre_tags || [],
        instagram: match.instagram || "",
        spotify: match.spotify || "",
        soundcloud: match.soundcloud || "",
        youtube: match.youtube || "",
        tiktok: match.tiktok || "",
        facebook: match.facebook || "",
        twitter: match.twitter || "",
        website: match.website || "",
      });
    } catch {} finally {
      setLoading(false);
    }
  }, [currentUser?.email]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleClaim = async () => {
    if (!profile) return;
    setClaiming(true);
    setMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/artists/${profile.slug}/claim`, {
        method: "POST",
        headers,
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: "success", text: "Profile claimed! You can now edit it." });
        setProfile(p => ({ ...p, is_claimed: true }));
      } else {
        setMsg({ type: "error", text: data.detail || "Claim failed" });
      }
    } catch {
      setMsg({ type: "error", text: "Network error" });
    } finally {
      setClaiming(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/artists/${profile.slug}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: "success", text: "Profile saved!" });
        setProfile(p => ({ ...p, ...form }));
      } else {
        setMsg({ type: "error", text: data.detail || "Save failed" });
      }
    } catch {
      setMsg({ type: "error", text: "Network error" });
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.genre_tags.includes(tag)) {
      setForm(f => ({ ...f, genre_tags: [...f.genre_tags, tag] }));
      setTagInput("");
    }
  };

  const removeTag = (tag) => {
    setForm(f => ({ ...f, genre_tags: f.genre_tags.filter(t => t !== tag) }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="artist-profile-loading">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
      </div>
    );
  }

  // No profile found — let them create one
  if (!profile) {
    const handleCreate = async () => {
      if (!createName.trim()) {
        setMsg({ type: "error", text: "Enter your artist name." });
        return;
      }
      setCreating(true);
      setMsg(null);
      try {
        const res = await fetch(`${BACKEND_URL}/api/artists/create-self`, {
          method: "POST",
          headers,
          body: JSON.stringify({ artist_name: createName.trim() }),
        });
        const data = await res.json();
        if (res.ok) {
          setMsg({ type: "success", text: "Profile created!" });
          fetchProfile();
        } else {
          setMsg({ type: "error", text: data.detail || "Failed to create profile" });
        }
      } catch {
        setMsg({ type: "error", text: "Network error" });
      } finally {
        setCreating(false);
      }
    };

    return (
      <div className="text-center py-16" data-testid="no-artist-profile">
        <Music className="w-12 h-12 text-white/10 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Set Up Your Artist Profile</h2>
        <p className="text-white/40 text-sm max-w-md mx-auto mb-6">
          Create your public artist page to showcase your music, bio, and social links.
        </p>

        {msg && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm max-w-sm mx-auto mb-4 ${
            msg.type === "success" ? "bg-green-500/10 border border-green-500/20 text-green-400"
              : "bg-red-500/10 border border-red-500/20 text-red-400"
          }`}>
            {msg.type === "success" ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {msg.text}
          </div>
        )}

        <div className="flex flex-col items-center gap-3 max-w-sm mx-auto">
          <input
            value={createName}
            onChange={e => setCreateName(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500/50 transition text-center"
            placeholder="Your artist / DJ name"
            data-testid="create-artist-name"
          />
          <button
            onClick={handleCreate}
            disabled={creating}
            className="w-full bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white font-semibold px-6 py-3 rounded-xl transition flex items-center justify-center gap-2"
            data-testid="create-profile-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {creating ? "Creating..." : "Create Artist Profile"}
          </button>
        </div>
      </div>
    );
  }

  const canEdit = profile.is_claimed || currentUser?.is_admin;
  const profileUrl = `${window.location.origin}/artist/${profile.slug}`;

  return (
    <div className="space-y-6" data-testid="artist-profile-tab">
      {/* Profile Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            {profile.artist_name}
            {profile.is_member && (
              <span className="inline-flex items-center gap-1 bg-amber-400/20 text-amber-400 text-xs font-bold px-2 py-0.5 rounded-full border border-amber-400/30">
                <Crown className="w-3 h-3" /> MEMBER
              </span>
            )}
          </h2>
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1 mt-1 transition"
            data-testid="view-profile-link"
          >
            View Public Profile <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {!profile.is_claimed && !currentUser?.is_admin && (
          <button
            onClick={handleClaim}
            disabled={claiming}
            className="flex items-center gap-2 bg-amber-400/20 hover:bg-amber-400/30 text-amber-400 border border-amber-400/30 font-semibold px-5 py-2.5 rounded-xl transition text-sm disabled:opacity-50"
            data-testid="claim-btn"
          >
            <CheckCircle className="w-4 h-4" /> {claiming ? "Claiming..." : "Claim This Profile"}
          </button>
        )}
      </div>

      {/* Status Message */}
      {msg && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm ${
          msg.type === "success"
            ? "bg-green-500/10 border border-green-500/20 text-green-400"
            : "bg-red-500/10 border border-red-500/20 text-red-400"
        }`} data-testid="profile-msg">
          {msg.type === "success" ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      {/* Not Claimed Notice */}
      {!canEdit && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-amber-400 text-sm" data-testid="claim-notice">
          <p className="font-medium mb-1">Claim your profile to edit it</p>
          <p className="text-amber-400/70 text-xs">Click "Claim This Profile" above to start customizing your artist page.</p>
        </div>
      )}

      {/* Edit Form */}
      {canEdit && (
        <div className="space-y-5">
          {/* Photo Upload */}
          <div>
            <label className="text-white/50 text-xs mb-1.5 block">Profile Photo</label>
            <div className="flex gap-4 items-center">
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-white/5 shrink-0 border border-white/10 relative group">
                {form.photo_url ? (
                  <img src={form.photo_url} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Music className="w-8 h-8 text-white/15" />
                  </div>
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition cursor-pointer">
                  <Camera className="w-5 h-5 text-white" />
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) {
                        setMsg({ type: "error", text: "Image must be under 5MB" });
                        return;
                      }
                      setUploading(true);
                      setMsg(null);
                      try {
                        const formData = new FormData();
                        formData.append("file", file);
                        const res = await fetch(`${BACKEND_URL}/api/artists/upload-photo`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${authToken}` },
                          body: formData,
                        });
                        const data = await res.json();
                        if (res.ok) {
                          const photoUrl = `${BACKEND_URL}/api/artists/photo/${data.path.replace("djsandmcmedia/artists/", "")}`;
                          setForm(f => ({ ...f, photo_url: photoUrl }));
                          setMsg({ type: "success", text: "Photo uploaded!" });
                        } else {
                          setMsg({ type: "error", text: data.detail || "Upload failed" });
                        }
                      } catch {
                        setMsg({ type: "error", text: "Upload failed" });
                      } finally {
                        setUploading(false);
                      }
                    }}
                    data-testid="photo-upload-input"
                  />
                </label>
              </div>
              <div>
                <p className="text-white/60 text-sm font-medium mb-1">
                  {uploading ? "Uploading..." : "Click photo to upload"}
                </p>
                <p className="text-white/30 text-xs">JPG, PNG, WebP or GIF. Max 5MB.</p>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className="text-white/50 text-xs mb-1.5 block">Bio</label>
            <textarea
              value={form.bio}
              onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
              rows={4}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500/50 transition resize-y"
              placeholder="Tell fans about yourself..."
              data-testid="input-bio"
            />
          </div>

          {/* Location */}
          <div>
            <label className="text-white/50 text-xs mb-1.5 block">Location</label>
            <input
              value={form.location}
              onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500/50 transition"
              placeholder="New York, NY"
              data-testid="input-location"
            />
          </div>

          {/* Gender (for verified check color) */}
          <div>
            <label className="text-white/50 text-xs mb-2 block">Gender (determines your verified check color)</label>
            <div className="flex gap-2">
              {[
                { value: "male", label: "Male", color: "blue", checkColor: "#3b82f6" },
                { value: "female", label: "Female", color: "pink", checkColor: "#ec4899" },
              ].map(g => (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, gender: g.value }))}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition border ${
                    form.gender === g.value
                      ? g.value === "male"
                        ? "bg-blue-500/15 border-blue-500/40 text-blue-400"
                        : "bg-pink-500/15 border-pink-500/40 text-pink-400"
                      : "bg-white/[0.03] border-white/10 text-white/40 hover:text-white/70"
                  }`}
                  data-testid={`gender-${g.value}`}
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4">
                    <path d="M9 12l2 2 4-4" stroke={form.gender === g.value ? g.checkColor : "#666"} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="10" fill={form.gender === g.value ? g.checkColor : "#666"} fillOpacity="0.15" stroke={form.gender === g.value ? g.checkColor : "#666"} strokeWidth="1.5"/>
                  </svg>
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {/* Genre Tags */}
          <div>
            <label className="text-white/50 text-xs mb-1.5 block">Genre Tags</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.genre_tags.map(tag => (
                <span key={tag} className="flex items-center gap-1.5 bg-purple-500/15 text-purple-400 text-xs px-3 py-1.5 rounded-full border border-purple-500/20">
                  {tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-white transition">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())}
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50 transition"
                placeholder="Add a genre tag..."
                data-testid="input-genre-tag"
              />
              <button
                onClick={addTag}
                className="bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 px-3 py-2.5 rounded-xl transition"
                data-testid="add-tag-btn"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Social Links */}
          <div>
            <label className="text-white/50 text-xs mb-3 block">Social Links</label>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/yourname" },
                { key: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@yourname" },
                { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/yourname" },
                { key: "twitter", label: "X (Twitter)", placeholder: "https://x.com/yourname" },
                { key: "spotify", label: "Spotify", placeholder: "https://open.spotify.com/artist/..." },
                { key: "soundcloud", label: "SoundCloud", placeholder: "https://soundcloud.com/yourname" },
                { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@yourname" },
                { key: "website", label: "Website", placeholder: "https://yourwebsite.com" },
              ].map(s => (
                <div key={s.key}>
                  <label className="text-white/30 text-[11px] mb-1 block">{s.label}</label>
                  <input
                    value={form[s.key]}
                    onChange={e => setForm(f => ({ ...f, [s.key]: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/50 transition"
                    placeholder={s.placeholder}
                    data-testid={`input-${s.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-40 text-white font-semibold px-6 py-3 rounded-xl transition"
            data-testid="save-profile-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : "Save Profile"}
          </button>
        </div>
      )}
    </div>
  );
};
